import React, {ReactNode, useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/registries/gtinsGrid/table';
import {GtinsGridFormattedRow, GtinsGridRawRow} from 'types/gtins';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (data: GtinsGridRawRow[] | undefined, t: TFunction) => GtinsGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const GtinsGridTable = ({configFunc, dataMapper, headerControls, exportConfig}: Props) => {
    const {t} = useTranslation();
    const {data, pageNumber, isLoading, filters, sortings} = useStore(tableState$);
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);
    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );

    return (
        <>
            <Gate />
            <Table
                columns={columns}
                data={mappedData}
                hasNext={data?.hasNext}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                isLoading={isLoading}
                exportConfig={exportConfig}
                headerControls={headerControls}
                userConfig={{
                    gridName: 'gtinsGrid',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
            />
        </>
    );
};
